import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  FilterList,
  MoreVert,
  Edit,
  Delete,
  Close,
  FileDownload,
  FolderZip,
  Assessment,
  Receipt as ReceiptIcon,
  CalendarToday,
  TodayOutlined,
} from '@mui/icons-material';
import {
  ExpenseResponseDTO,
  ExpenseCategory,
  ExpenseStatus,
  ExpenseCategoryLabels,
  ExpenseStatusLabels,
  ExpenseCategoryColors,
  MonthlyReportDTO,
  ProjectMarginDTO,
} from '../../types/expense.types';
import { Projet } from '../../types';
import expenseService from '../../services/expenseService';
import accountingService from '../../services/accountingService';
import { ExpenseForm } from './ExpenseForm';
import { ExportReportDialog } from './ExportReportDialog';
import { ZipExportDialog } from './ZipExportDialog';
import { AccountingDashboard } from './AccountingDashboard';
import { ProjectMarginsView } from './ProjectMarginsView';
import { useNotification } from '../../contexts/NotificationContext';

interface ExpenseManagerProps {
  projects?: Projet[];
}

/**
 * Composant principal de gestion des dépenses
 * Affiche la liste des dépenses, les filtres, et la jauge de marge
 */
export const ExpenseManager: React.FC<ExpenseManagerProps> = ({ projects = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSuccess, showConfirm } = useNotification();

  // État
  const [expenses, setExpenses] = useState<ExpenseResponseDTO[]>([]);
  const [filteredExpenses, setFilteredExpenses] = useState<ExpenseResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filtres
  const [filterCategory, setFilterCategory] = useState<ExpenseCategory | 'ALL'>('ALL');
  const [filterStatus, setFilterStatus] = useState<ExpenseStatus | 'ALL'>('ALL');
  const [filterPeriod, setFilterPeriod] = useState<'CURRENT_MONTH' | 'LAST_MONTH' | 'ALL'>(
    'ALL'
  );
  const [filterProject, setFilterProject] = useState<number | 'ALL'>('ALL');

  // Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ExpenseResponseDTO | undefined>();
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');

  // Dialog Export
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [openZipDialog, setOpenZipDialog] = useState(false);

  // Menu contextuel
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuExpense, setMenuExpense] = useState<ExpenseResponseDTO | null>(null);

  // Statistiques pour AccountingDashboard
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReportDTO | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Sélecteur de période pour le dashboard comptable
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState<'MONTH' | 'YEAR'>('MONTH');

  // Onglets pour basculer entre vue globale et vue par projet
  const [currentTab, setCurrentTab] = useState(0);

  // Données pour la vue par projet
  const [projectMargins, setProjectMargins] = useState<ProjectMarginDTO[]>([]);
  const [projectMarginsLoading, setProjectMarginsLoading] = useState(false);

  useEffect(() => {
    loadExpenses();
    loadFinancialData();
    loadProjectMargins();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [expenses, filterCategory, filterStatus, filterPeriod, filterProject]);

  const loadExpenses = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await expenseService.getAll();
      setExpenses(data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des dépenses:', err);
      setError('Erreur lors du chargement des dépenses');
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialData = async (year?: number, month?: number, mode?: 'MONTH' | 'YEAR') => {
    setReportLoading(true);
    try {
      const targetYear = year ?? selectedYear;
      const targetMonth = month ?? selectedMonth;
      const targetMode = mode ?? viewMode;

      let report;
      if (targetMode === 'YEAR') {
        // Vue annuelle : utiliser la méthode getYearlyReport
        report = await accountingService.getYearlyReport(targetYear);
      } else {
        // Vue mensuelle
        report = await accountingService.getMonthlyReport(targetYear, targetMonth);
      }
      setMonthlyReport(report);
    } catch (err) {
      console.error('Erreur lors du chargement des données financières:', err);
    } finally {
      setReportLoading(false);
    }
  };

  const loadProjectMargins = async () => {
    setProjectMarginsLoading(true);
    try {
      const margins = await accountingService.getAllProjectMargins();
      setProjectMargins(margins);
    } catch (err) {
      console.error('Erreur lors du chargement des marges par projet:', err);
    } finally {
      setProjectMarginsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...expenses];

    // Filtre par catégorie
    if (filterCategory !== 'ALL') {
      filtered = filtered.filter((exp) => exp.category === filterCategory);
    }

    // Filtre par statut
    if (filterStatus !== 'ALL') {
      filtered = filtered.filter((exp) => exp.status === filterStatus);
    }

    // Filtre par période
    if (filterPeriod !== 'ALL') {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      filtered = filtered.filter((exp) => {
        const expDate = new Date(exp.date);
        const expMonth = expDate.getMonth();
        const expYear = expDate.getFullYear();

        if (filterPeriod === 'CURRENT_MONTH') {
          return expMonth === currentMonth && expYear === currentYear;
        } else if (filterPeriod === 'LAST_MONTH') {
          const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          return expMonth === lastMonth && expYear === lastMonthYear;
        }
        return true;
      });
    }

    // Filtre par projet
    if (filterProject !== 'ALL') {
      filtered = filtered.filter((exp) => exp.projetId === filterProject);
    }

    setFilteredExpenses(filtered);
  };

  const handleCreate = () => {
    setDialogMode('create');
    setSelectedExpense(undefined);
    setOpenDialog(true);
  };

  const handleEdit = (expense: ExpenseResponseDTO) => {
    setDialogMode('edit');
    setSelectedExpense(expense);
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDelete = async (expense: ExpenseResponseDTO) => {
    const confirmed = await showConfirm({
      title: 'Supprimer la dépense',
      message: `Êtes-vous sûr de vouloir supprimer la dépense "${expense.label}" ?`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error',
    });

    if (confirmed) {
      try {
        await expenseService.delete(expense.id);
        await loadExpenses();
        await loadFinancialData();
        showSuccess('Dépense supprimée avec succès');
      } catch (err: any) {
        console.error('Erreur lors de la suppression:', err);
        setError('Erreur lors de la suppression de la dépense');
      }
    }
    handleMenuClose();
  };

  const handleSuccess = async () => {
    setOpenDialog(false);
    await loadExpenses();
    await loadFinancialData();
    await loadProjectMargins();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, expense: ExpenseResponseDTO) => {
    setAnchorEl(event.currentTarget);
    setMenuExpense(expense);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuExpense(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const getTotalFiltered = () => {
    return filteredExpenses.reduce((sum, exp) => sum + exp.amountTtc, 0);
  };

  /**
   * Handler pour le succès du téléchargement ZIP
   */
  const handleZipDownloadSuccess = (message: string) => {
    showSuccess(message);
  };

  /**
   * Handler pour le succès de l'envoi par email
   */
  const handleZipEmailSuccess = (message: string) => {
    showSuccess(message);
  };

  /**
   * Obtenir l'année et le mois selon le filtre actuel
   */
  const getCurrentPeriod = (): { year: number; month: number } => {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth() + 1;

    if (filterPeriod === 'LAST_MONTH') {
      if (month === 1) {
        month = 12;
        year = year - 1;
      } else {
        month = month - 1;
      }
    }

    return { year, month };
  };

  // Rendu mobile (Cards)
  const renderMobileView = () => (
    <Stack spacing={2}>
      {filteredExpenses.map((expense) => (
        <Card key={expense.id} elevation={2}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {expense.label}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip
                    label={ExpenseCategoryLabels[expense.category]}
                    size="small"
                    sx={{
                      backgroundColor: `${ExpenseCategoryColors[expense.category]}20`,
                      color: ExpenseCategoryColors[expense.category],
                    }}
                  />
                  <Chip
                    label={ExpenseStatusLabels[expense.status]}
                    size="small"
                    color={expense.status === ExpenseStatus.VALIDATED ? 'success' : 'warning'}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {formatDate(expense.date)}
                  {expense.projetTitre && ` • ${expense.projetTitre}`}
                </Typography>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                  {formatCurrency(expense.amountTtc)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  HT: {formatCurrency(expense.amountHt)} | TVA: {formatCurrency(expense.amountTva)}
                </Typography>
              </Box>
              <IconButton onClick={(e) => handleMenuOpen(e, expense)}>
                <MoreVert />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  // Rendu desktop (Table)
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Libellé</TableCell>
            <TableCell>Catégorie</TableCell>
            <TableCell>Projet</TableCell>
            <TableCell align="right">HT</TableCell>
            <TableCell align="right">TVA</TableCell>
            <TableCell align="right">TTC</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredExpenses.map((expense) => (
            <TableRow key={expense.id} hover>
              <TableCell>{formatDate(expense.date)}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {expense.label}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={ExpenseCategoryLabels[expense.category]}
                  size="small"
                  sx={{
                    backgroundColor: `${ExpenseCategoryColors[expense.category]}20`,
                    color: ExpenseCategoryColors[expense.category],
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {expense.projetTitre || '-'}
                </Typography>
              </TableCell>
              <TableCell align="right">{formatCurrency(expense.amountHt)}</TableCell>
              <TableCell align="right">{formatCurrency(expense.amountTva)}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(expense.amountTtc)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={ExpenseStatusLabels[expense.status]}
                  size="small"
                  color={expense.status === ExpenseStatus.VALIDATED ? 'success' : 'warning'}
                />
              </TableCell>
              <TableCell align="center">
                <IconButton size="small" onClick={(e) => handleMenuOpen(e, expense)}>
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* En-tête */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Gestion des Dépenses
        </Typography>

        {/* Boutons d'actions - Version mobile */}
        {isMobile && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              fullWidth
              sx={{ flex: 1, minWidth: '100%' }}
            >
              Nouvelle dépense
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => setOpenExportDialog(true)}
              sx={{ flex: 1 }}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FolderZip />}
              onClick={() => setOpenZipDialog(true)}
              sx={{ flex: 1 }}
            >
              Export ZIP
            </Button>
          </Stack>
        )}

        {/* Boutons d'actions - Version desktop */}
        {!isMobile && (
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => setOpenExportDialog(true)}
            >
              Exporter CSV
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FolderZip />}
              onClick={() => setOpenZipDialog(true)}
            >
              Export complet (ZIP)
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
              Nouvelle dépense
            </Button>
          </Stack>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Onglets pour basculer entre vues */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tab
            icon={<Assessment />}
            iconPosition="start"
            label="Vue Globale"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<ReceiptIcon />}
            iconPosition="start"
            label="Par Projet"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Paper>

      {/* Contenu selon l'onglet sélectionné */}
      {currentTab === 0 ? (
        // Vue Globale
        <Grid container spacing={3}>
          {/* Sélecteur de période pour le dashboard comptable */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 0 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Période d'analyse
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
                  {/* Boutons de basculement Vue Mensuelle / Annuelle */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant={viewMode === 'MONTH' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<TodayOutlined />}
                      onClick={() => {
                        setViewMode('MONTH');
                        loadFinancialData(selectedYear, selectedMonth, 'MONTH');
                      }}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Vue Mensuelle
                    </Button>
                    <Button
                      variant={viewMode === 'YEAR' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<CalendarToday />}
                      onClick={() => {
                        setViewMode('YEAR');
                        loadFinancialData(selectedYear, selectedMonth, 'YEAR');
                      }}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Vue Annuelle
                    </Button>
                  </Box>

                  {/* Sélecteur d'année */}
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Année</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Année"
                      onChange={(e) => {
                        const year = Number(e.target.value);
                        setSelectedYear(year);
                        loadFinancialData(year, selectedMonth, viewMode);
                      }}
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Sélecteur de mois (uniquement en vue mensuelle) */}
                  {viewMode === 'MONTH' && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Mois</InputLabel>
                      <Select
                        value={selectedMonth}
                        label="Mois"
                        onChange={(e) => {
                          const month = Number(e.target.value);
                          setSelectedMonth(month);
                          loadFinancialData(selectedYear, month, viewMode);
                        }}
                      >
                        {[
                          { value: 1, label: 'Janvier' },
                          { value: 2, label: 'Février' },
                          { value: 3, label: 'Mars' },
                          { value: 4, label: 'Avril' },
                          { value: 5, label: 'Mai' },
                          { value: 6, label: 'Juin' },
                          { value: 7, label: 'Juillet' },
                          { value: 8, label: 'Août' },
                          { value: 9, label: 'Septembre' },
                          { value: 10, label: 'Octobre' },
                          { value: 11, label: 'Novembre' },
                          { value: 12, label: 'Décembre' },
                        ].map((m) => (
                          <MenuItem key={m.value} value={m.value}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Bouton retour au mois actuel */}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      const now = new Date();
                      setSelectedYear(now.getFullYear());
                      setSelectedMonth(now.getMonth() + 1);
                      setViewMode('MONTH');
                      loadFinancialData(now.getFullYear(), now.getMonth() + 1, 'MONTH');
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Aujourd'hui
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Dashboard comptable complet */}
          {monthlyReport && (
            <Grid item xs={12}>
              <AccountingDashboard report={monthlyReport} loading={reportLoading} />
            </Grid>
          )}

        {/* Filtres */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <FilterList />
              <Typography variant="h6">Filtres</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={filterCategory}
                    label="Catégorie"
                    onChange={(e) => setFilterCategory(e.target.value as any)}
                  >
                    <MenuItem value="ALL">Toutes</MenuItem>
                    {Object.values(ExpenseCategory).map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {ExpenseCategoryLabels[cat]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Statut"
                    onChange={(e) => setFilterStatus(e.target.value as any)}
                  >
                    <MenuItem value="ALL">Tous</MenuItem>
                    {Object.values(ExpenseStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {ExpenseStatusLabels[status]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Période</InputLabel>
                  <Select
                    value={filterPeriod}
                    label="Période"
                    onChange={(e) => setFilterPeriod(e.target.value as any)}
                  >
                    <MenuItem value="CURRENT_MONTH">Mois en cours</MenuItem>
                    <MenuItem value="LAST_MONTH">Mois dernier</MenuItem>
                    <MenuItem value="ALL">Toutes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Projet</InputLabel>
                  <Select
                    value={filterProject}
                    label="Projet"
                    onChange={(e) => setFilterProject(e.target.value as any)}
                  >
                    <MenuItem value="ALL">Tous</MenuItem>
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.titre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredExpenses.length} dépense(s) • Total: {formatCurrency(getTotalFiltered())}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Liste des dépenses */}
        <Grid item xs={12}>
          {loading ? (
            <Typography>Chargement...</Typography>
          ) : filteredExpenses.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Aucune dépense trouvée</Typography>
            </Paper>
          ) : isMobile ? (
            renderMobileView()
          ) : (
            renderDesktopView()
          )}
        </Grid>
      </Grid>
      ) : (
        // Vue Par Projet
        <Box>
          <ProjectMarginsView projects={projectMargins} loading={projectMarginsLoading} />
        </Box>
      )}

      {/* Dialog Formulaire */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {dialogMode === 'edit' ? 'Modifier la dépense' : 'Nouvelle dépense'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <ExpenseForm
            mode={dialogMode}
            initialData={selectedExpense}
            projects={projects}
            onSuccess={handleSuccess}
            onCancel={() => setOpenDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Menu Contextuel */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuExpense && handleEdit(menuExpense)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuExpense && handleDelete(menuExpense)}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Supprimer</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog Export CSV */}
      <ExportReportDialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
      />

      {/* Dialog Export ZIP complet */}
      <ZipExportDialog
        open={openZipDialog}
        onClose={() => setOpenZipDialog(false)}
        year={getCurrentPeriod().year}
        month={getCurrentPeriod().month}
        onDownloadSuccess={handleZipDownloadSuccess}
        onEmailSuccess={handleZipEmailSuccess}
      />
    </Box>
  );
};

export default ExpenseManager;
